import React, { useEffect } from "react";
import { css, useTheme } from "@emotion/react";
import ContentCombine from "core/Components/ContentCombine";
import Header from "elements/Components/FullImage/components/Header";
import SectionContainer from "core/Components/SectionContainer";
import Wrapper from "core/Components/Wrapper";
import Image from "core/Components/Image";
import RelatedPosts from "pages/Components/BlogPost/RelatedPosts";
import moment from "moment";
import SocialsVerticalList from "core/Components/Socials/SocialsVerticalList";
import SocialsHorizontalList from "core/Components/Socials/SocialsHorizontalList";
import SimpleBannerWithImage from "elements/Components/SimpleBannerWithImage/SimpleBannerWithImage";
import { useAppState } from "core/contexts/AppContext";

type Props = {
    data: BlogPost
};

const BlogPost: React.FC<Props> = ({ data }) => {
    const theme = useTheme();
    const [, setAppState] = useAppState();
    const publishedDate = moment(data.publishedDate).format('MMM DD \'YY');

    useEffect(() => {
        setAppState({
            currentPageName: data.title
        });
    }, [data.title, setAppState]);

    const containerStyle = css`
        display: flex;
    `;

    const headingContainer = css`
        width: 100%;
        padding: 68px 270px 0;
        display: flex;
        flex-direction: column;
        background-color: ${theme.colours.alabaster2};

        ${theme.breakpoints.up('3xl')} {
            padding-top: 45px;
            padding-bottom: 40px;
        }

        ${theme.breakpoints.only('xxl')} {
            padding: 38px 215px 25px;
        }

        ${theme.breakpoints.only('xl')} {
            padding: 35px 141px 40px;
        }

        ${theme.breakpoints.only('lg')} {
            padding: 36px 139px 24px;
        }

        ${theme.breakpoints.only('md')} {
            padding: 27px 45px 30px;
        }

        ${theme.breakpoints.down('sm')} {
            padding-top: 29px;
        }

        ${theme.breakpoints.only('sm')} {
            padding-bottom: 18px;
        }

        ${theme.breakpoints.only('sm')} {
            padding-left: 44px;
            padding-right: 43px;
        }

        ${theme.breakpoints.down('xs')} {
            padding-left: 22px;
            padding-right: 23px;
            padding-bottom: 24px;
        }
    `;

    const authorStyle = css`
        padding-right: 20px;
        padding-top: 15px;
        font-size: 18px;
        line-height: 26px;
        color: ${theme.colours.gray2};
        font-weight: ${theme.fonts.weights.light};
        font-style: italic;

        ${theme.breakpoints.between('3xl', '4xl')} {
            padding-top: 27px;
        }

        ${theme.breakpoints.only('xxl')} {
            padding-top: 34px;
        }

        ${theme.breakpoints.only('xl')} {
            padding-top: 6px;
        }

        ${theme.breakpoints.between('md', 'lg')} {
            padding-top: 29px;
        }

        ${theme.breakpoints.down('sm')} {
            padding-top: 28px;
            font-size: 14px;
        }
    `;

    const authorThumbnailStyle = css`
        width: 24px;
        height: 24px;
        margin-right: 8px;
        border-radius: 50%;
    `;

    const contentContainerStyle = css`
        position: relative;
        width: 100%;
        color: ${theme.colours.shipGray};
        padding: 41px 270px 130px;

        ${theme.breakpoints.up('5xl')} {
            padding-top: 78px;
        }

        ${theme.breakpoints.up('5xl')} {
            min-height: 674px;
        }

        ${theme.breakpoints.between('lg', '4xl')} {
            min-height: 412px;
        }

        ${theme.breakpoints.only('xxl')} {
            padding-right: 215px;
            padding-left: 215px;
        }

        ${theme.breakpoints.only('xl')} {
            padding-right: 141px;
            padding-left: 141px;
        }

        ${theme.breakpoints.only('lg')} {
            padding-bottom: 72px;
            padding-right: 139px;
            padding-left: 139px;
        }

        ${theme.breakpoints.only('md')} {
            padding-left: 45px;
            padding-right: 45px;
            padding-bottom: 120px;
        }

        ${theme.breakpoints.only('sm')} {
            padding: 28px 43px 138px 44px;
        }

        ${theme.breakpoints.down('xs')} {
            padding-left: 22px;
            padding-right: 23px;
        }
        
        iframe {
            width: 792px;
            height: 528px;
            
            ${theme.breakpoints.only('xxl')} {
                width: 908px;
                height: 605px;
            }

            ${theme.breakpoints.only('xl')} {
                width: 792px;
                height: 528px;
            }

            ${theme.breakpoints.only('lg')} {
                width: 746px;
                height: 497px;
            }

            ${theme.breakpoints.only('md')} {
                width: 678px;
                height: 452px;
            }

            ${theme.breakpoints.only('sm')} {
                width: 478px;
                height: 319px;
            }

            ${theme.breakpoints.down('xs')} {
                width: 320px;
                height: 213px;
            }
        }
        
        img {
            width: 792px;
            
            ${theme.breakpoints.only('xxl')} {
                width: 908px;
            }

            ${theme.breakpoints.only('xl')} {
                width: 792px;
            }

            ${theme.breakpoints.only('lg')} {
                width: 746px;
            }

            ${theme.breakpoints.only('md')} {
                width: 678px;
            }

            ${theme.breakpoints.only('sm')} {
                width: 478px;
            }

            ${theme.breakpoints.down('xs')} {
                width: 320px;
            }
        }
    `;

    const titleStyle = css`
        justify-content: flex-start;
    `;

    const subheaderStyle = css`
        margin-bottom: 22px;
        justify-content: flex-start;
        color: ${theme.colours.scorpion2};
    `;

    const socialShareDesktopStyle = css`
        position: absolute;
        top: 40px;
        right: 80px;

        ${theme.breakpoints.only('xl')} {
            right: 37px;
        }

        ${theme.breakpoints.only('lg')} {
            right: 27px;
        }

        ${theme.breakpoints.down('md')} {
            display: none;
        }
    `;

    const socialsHorizontalList = css`
        ${theme.breakpoints.up('lg')} {
            display: none;
        }
    `;

    return (
        <SectionContainer css={containerStyle}>
            <Wrapper useMaxWidth={false} flexDirection={"column"}>
                {!data.hideBanner && (
                    <SimpleBannerWithImage image={data.banner} />
                )}
                {data.title && (
                    <div css={headingContainer}>
                        <Header css={titleStyle}>
                            {data.title}
                        </Header>
                        <div css={authorStyle}>
                            {data.author?.webpWithFallbackPortrait && (
                                <Image
                                    css={authorThumbnailStyle}
                                    image={data.author?.webpWithFallbackPortrait}
                                />
                            )}
                            {data.author?.name && (
                                <div>Posted by {data.author.name},&nbsp;</div>
                            )}
                            <div>{publishedDate}</div>
                        </div>
                    </div>
                )}
                <div css={contentContainerStyle}>
                    <SocialsVerticalList
                        css={socialShareDesktopStyle}
                        emailSubject={data.title}
                    />
                    {data?.subheader && (
                        <ContentCombine
                            css={subheaderStyle}
                            Tag={'h5'}
                        >
                            {data.subheader}
                        </ContentCombine>
                    )}
                    <ContentCombine fontSizeType={'9'}>
                        {data.content}
                    </ContentCombine>
                </div>
                <SocialsHorizontalList
                    title={data.title}
                    css={socialsHorizontalList}
                />
                <RelatedPosts posts={data.relatedPosts} />
            </Wrapper>
        </SectionContainer>
    );
}

export default BlogPost;
